import React, { createContext, useContext, useState, useEffect } from "react";
import { apiClient } from "../services/api.js";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const initAuth = async () => {
      if (token) {
        apiClient.defaults.headers.common["Authorization"] = `Bearer ${token}`;
        try {
          await fetchUserProfile();
        } catch (error) {
          console.error("Failed to fetch user profile:", error);
        }
      }
      if (isMounted) {
        setLoading(false);
      }
    };

    initAuth();

    return () => {
      isMounted = false;
    };
  }, []);

  const fetchUserProfile = async () => {
    try {
      const response = await apiClient.get("/auth/profile");
      setUser(response.data.user);
    } catch (error) {
      console.error("Failed to fetch user profile:", error);
      logout();
      throw error;
    }
  };

  const login = async (username, password, recaptchaToken) => {
    try {
      const payload = { username, password };
      if (import.meta.env.VITE_RECAPTCHA_SITE_KEY && recaptchaToken) {
        payload.recaptcha_token = recaptchaToken;
      }
      const response = await apiClient.post("/api/auth/login", payload);

      const { access_token, user } = response.data;

      setToken(access_token);
      setUser(user);
      localStorage.setItem("token", access_token);
      apiClient.defaults.headers.common[
        "Authorization"
      ] = `Bearer ${access_token}`;

      return { success: true };
    } catch (error) {
      console.error("Login failed:", error);
      return {
        success: false,
        error: error.response?.data?.error || "Login failed",
      };
    }
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem("token");
    delete apiClient.defaults.headers.common["Authorization"];
  };

  const isAdmin = () => {
    return user?.is_admin || false;
  };

  const value = {
    user,
    token,
    loading,
    login,
    logout,
    isAdmin,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
